import { useState } from 'react';
import { formatDecimal } from '../../lib/utils';
import { Plus, Search, Download, Eye, X, FileText, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import useSWR, { mutate } from 'swr';
import apiClient from '../../core/apiClient';
import { useAuth } from '../../auth/AuthContext';
import { StatCardGrid, type StatCardItem } from '../../components/StatCardGrid';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

interface Invoice {
  id: string;
  invoiceNumber: string;
  clientId: string;
  description: string;
  taxBase: number;
  taxAmount: number;
  total: number;
  status: 'DRAFT' | 'EMITTED' | 'SENT' | 'PARTIAL' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  invoiceDate: string;
  dueDate: string;
}

interface CreateInvoiceForm {
  invoiceNumber: string;
  baseAmount: string;
  taxAmount: string;
  dueDate: string;
  description: string;
}

const fetcher = (url: string) => 
  apiClient.get(url).then((res) => res.data);

export default function Invoices() {
  const { user } = useAuth();
  const companyId = user?.companyId;
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [formData, setFormData] = useState<CreateInvoiceForm>({
    invoiceNumber: '',
    baseAmount: '',
    taxAmount: '',
    dueDate: '',
    description: '',
  });

  console.log('[v0] Invoices component - companyId:', companyId);

  const skip = (page - 1) * 20;
  const { data: response, isLoading, error } = useSWR(
    companyId ? `/finance/invoices/company/${companyId}?skip=${skip}&take=20${status ? `&status=${status.toUpperCase()}` : ''}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  console.log('[v0] Invoices SWR - isLoading:', isLoading, 'error:', error, 'response:', response);

  const invoices = response?.data || [];
  const total = response?.pagination?.total || 0;

  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    
    if (!formData.invoiceNumber.trim() || !formData.baseAmount) {
      setCreateError('Por favor completa los campos requeridos');
      return;
    }

    setIsCreating(true);
    try {
      const payload = {
        invoiceNumber: formData.invoiceNumber,
        baseAmount: parseFloat(formData.baseAmount),
        taxAmount: parseFloat(formData.taxAmount || '0'),
        dueDate: formData.dueDate,
        description: formData.description,
      };

      await apiClient.post('/finance/electronic', payload);

      mutate(`/finance/invoices/company/${companyId}?skip=0&take=20${status ? `&status=${status.toUpperCase()}` : ''}`);

      setShowCreateModal(false);
      setFormData({
        invoiceNumber: '',
        baseAmount: '',
        taxAmount: '',
        dueDate: '',
        description: '',
      });
    } catch (err: any) {
      setCreateError(err?.response?.data?.message || 'Error en crear la factura');
    } finally {
      setIsCreating(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const filteredInvoices = invoices.filter((inv: Invoice) =>
    inv.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'EMITTED': case 'SENT': return 'bg-blue-100 text-blue-800';
      case 'PARTIAL': return 'bg-yellow-100 text-yellow-800';
      case 'PAID': return 'bg-green-100 text-green-800';
      case 'OVERDUE': return 'bg-red-100 text-red-800';
      case 'CANCELLED': return 'bg-gray-200 text-gray-500';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      DRAFT: 'Esborrany',
      EMITTED: 'Emesa',
      SENT: 'Enviada',
      PARTIAL: 'Parcial',
      PAID: 'Pagada',
      OVERDUE: 'Vencida',
      CANCELLED: 'Cancel·lada',
    };
    return labels[status] || status;
  };

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">Error carregant les factures: {error?.message || 'Error desconegut'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtres i Accions */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <input
              type="text"
              placeholder="Cerca factura..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-input rounded-md text-sm"
            />
          </div>
        </div>
        <ExcelFilterDropdown
          label="Estat"
          options={[
            { value: 'DRAFT', label: 'Esborrany' },
            { value: 'EMITTED', label: 'Emesa' },
            { value: 'SENT', label: 'Enviada' },
            { value: 'PARTIAL', label: 'Parcial' },
            { value: 'PAID', label: 'Pagada' },
            { value: 'OVERDUE', label: 'Vencida' },
            { value: 'CANCELLED', label: 'Cancel·lada' },
          ]}
          selected={status ? status.split(',') : []}
          onApply={(values) => setStatus(values.join(','))}
          className="mr-2"
        />
        <button 
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Nova Factura
        </button>
      </div>

      <div className="border border-border rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-[12px] font-medium text-muted-foreground">Número</th>
              <th className="px-4 py-3 text-left text-[12px] font-medium text-muted-foreground">Descripció</th>
              <th className="px-4 py-3 text-left text-[12px] font-medium text-muted-foreground">Import</th>
              <th className="px-4 py-3 text-left text-[12px] font-medium text-muted-foreground">Data</th>
              <th className="px-4 py-3 text-left text-[12px] font-medium text-muted-foreground">Venciment</th>
              <th className="px-4 py-3 text-left text-[12px] font-medium text-muted-foreground">Estat</th>
              <th className="px-4 py-3 text-left text-[12px] font-medium text-muted-foreground">Accions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  Carregant...
                </td>
              </tr>
            ) : filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  No hi ha factures
                </td>
              </tr>
            ) : (
              filteredInvoices.map((invoice: Invoice) => (
                <tr key={invoice.id} className="border-b border-border hover:bg-muted/50">
                  <td className="px-4 py-3 font-medium">{invoice.invoiceNumber}</td>
                  <td className="px-4 py-3">{invoice.description || '—'}</td>
                  <td className="px-4 py-3">{formatDecimal((invoice.total ?? 0))} €</td>
                  <td className="px-4 py-3">{new Date(invoice.invoiceDate).toLocaleDateString('ca-ES')}</td>
                  <td className="px-4 py-3">{new Date(invoice.dueDate).toLocaleDateString('ca-ES')}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(invoice.status)}`}>
                      {getStatusLabel(invoice.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 flex gap-2">
                    <button className="p-1 hover:bg-muted rounded" title="Veure">
                      <Eye className="h-4 w-4" />
                    </button>
                    <button className="p-1 hover:bg-muted rounded" title="Descarregar">
                      <Download className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > 20 && (
        <div className="flex justify-center gap-2 mt-4">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="px-4 py-2 border rounded-md disabled:opacity-50"
          >
            Anterior
          </button>
          <span className="px-4 py-2">Pàgina {page}</span>
          <button
            disabled={page * 20 >= total}
            onClick={() => setPage(page + 1)}
            className="px-4 py-2 border rounded-md disabled:opacity-50"
          >
            Següent
          </button>
        </div>
      )}

      {/* Modal de crear factura */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-sm font-semibold">Nova Factura</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-muted-foreground hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateInvoice} className="p-6 space-y-4">
              {createError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                  {createError}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium mb-1">Número de Factura *</label>
                <input
                  type="text"
                  name="invoiceNumber"
                  placeholder="ex: FAC-2025-001"
                  value={formData.invoiceNumber}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1">Base Imponible (€) *</label>
                  <input
                    type="number"
                    name="baseAmount"
                    placeholder="0.00"
                    step="0.01"
                    value={formData.baseAmount}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">IVA (€)</label>
                  <input
                    type="number"
                    name="taxAmount"
                    placeholder="0.00"
                    step="0.01"
                    value={formData.taxAmount}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Data de Venciment</label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Descripció</label>
                <textarea
                  name="description"
                  placeholder="Descripció dels serveis o productes..."
                  value={formData.description}
                  onChange={handleFormChange}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel·lar
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                >
                  {isCreating ? 'Creant...' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
